import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, CheckCircle2, XCircle, Clock, Loader2, Plus, Building2, UserCog, ScrollText, Database, EyeOff } from 'lucide-react';
import { listCustomers, getBoardSummary, createCustomer } from '../../../services/apiClient';
import { TabContainer } from '../../../components/layout/TabNavigation';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import StatusChip from './StatusChip';
import UsersTab from './UsersTab';
import OverviewTab from './OverviewTab';
import TileAccessTab from './TileAccessTab';
import OnboardingTab from './OnboardingTab';
import AdminsPanel from './AdminsPanel';
import AuditLogPanel from './AuditLogPanel';
import GlobalCachePanel from './GlobalCachePanel';

// Top-level views, sibling to the per-tenant tab detail below — Admins,
// Audit Log and Cache are not scoped to any one tenant (admins aren't
// necessarily a member of a customer, and the cache/audit log span all of
// them), so they live here instead of squeezed into the tenant tab layout.
const TOP_LEVEL_VIEWS = [
  { id: 'tenants', icon: Building2 },
  { id: 'admins', icon: UserCog },
  { id: 'auditLog', icon: ScrollText },
  { id: 'cache', icon: Database },
];

/**
 * One tenant's at-a-glance status chips (Phase 5 board summary).
 */
const TenantStatusChips = ({ summary, t }) => {
  if (!summary) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-1.5">
      <StatusChip tone={summary.etl_reachable ? 'emerald' : 'red'} icon={summary.etl_reachable ? CheckCircle2 : XCircle}>
        {summary.etl_reachable ? t('board.etlReachable') : t('board.etlUnreachable')}
      </StatusChip>
      <StatusChip tone={summary.scheduled_data_enabled ? 'emerald' : 'amber'} icon={Clock}>
        {summary.scheduled_data_enabled ? t('board.scheduled') : t('board.notScheduled')}
      </StatusChip>
      {summary.last_job_status && (
        <StatusChip
          tone={
            summary.last_job_status === 'success'
              ? 'emerald'
              : summary.last_job_status === 'failed'
              ? 'red'
              : 'amber'
          }
        >
          {t('board.lastJob', { status: summary.last_job_status })}
        </StatusChip>
      )}
      {summary.active_job && (
        <StatusChip tone="blue" icon={Loader2} pulse>
          {t('board.jobRunning')}
        </StatusChip>
      )}
      <StatusChip tone="gray">{t('board.userCount', { count: summary.user_count })}</StatusChip>
    </div>
  );
};

/**
 * Admin-only page for managing which users belong to which customer (tenant).
 * Backed by backend/src/tenant_admin (GET/POST/DELETE .../customers[/{id}/users]).
 * Route-gated in index.jsx; see the ProtectedRoute sentinel note there.
 */
const TenantAdminPage = () => {
  const { t } = useTranslation('admin');
  const [topView, setTopView] = useState('tenants');
  const [customers, setCustomers] = useState([]);
  const [boardSummary, setBoardSummary] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [err, setErr] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [createErr, setCreateErr] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const visibleCustomers = showInactive ? customers : customers.filter((c) => c.is_active !== false);

  useEffect(() => {
    listCustomers()
      .then(setCustomers)
      .catch((e) => setErr(t('errors.listCustomers', { detail: e.message })));
  }, []);

  useEffect(() => {
    const loadBoardSummary = () => {
      getBoardSummary()
        .then((tenants) => setBoardSummary(Object.fromEntries(tenants.map((row) => [row.customer_id, row]))))
        .catch(() => {}); // non-fatal: chips just stay hidden for this refresh
    };
    loadBoardSummary();
    const interval = setInterval(loadBoardSummary, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateCustomer = async () => {
    setCreateErr(null);
    try {
      const created = await createCustomer(newCustomerId, newDisplayName);
      setCustomers((prev) => [...prev, created]);
      setSelectedId(created.customer_id);
      setShowCreateForm(false);
      setNewCustomerId('');
      setNewDisplayName('');
    } catch (e) {
      setCreateErr(t('errors.createCustomer', { detail: e.message }));
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-900">
        <Users className="w-5 h-5 text-primary-600" /> {t('tenants.title')}
      </h1>
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {TOP_LEVEL_VIEWS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTopView(id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              topView === id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {t(`nav.${id}`)}
          </button>
        ))}
      </div>
      {err && <div className="mb-4 text-sm text-error-600">{err}</div>}

      {topView === 'admins' && <AdminsPanel />}
      {topView === 'auditLog' && <AuditLogPanel customers={customers} />}
      {topView === 'cache' && <GlobalCachePanel />}

      {topView === 'tenants' && (
      <div className="flex gap-8">
        <div className="w-1/3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-gray-500">{t('tenants.customers')}</h2>
            {customers.some((c) => c.is_active === false) && (
              <button
                onClick={() => setShowInactive((v) => !v)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
              >
                <EyeOff className="w-3 h-3" />
                {showInactive ? t('tenants.hideInactive') : t('tenants.showInactive')}
              </button>
            )}
          </div>
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full mb-2 flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t('createTenant.button')}
            </button>
          ) : (
            <div className="mb-2 border border-gray-200 rounded-lg p-3 space-y-2 bg-white shadow-sm">
              {createErr && <div className="text-xs text-error-600">{createErr}</div>}
              <Input
                type="text"
                placeholder={t('createTenant.customerIdPlaceholder')}
                value={newCustomerId}
                onChange={(e) => setNewCustomerId(e.target.value)}
              />
              <Input
                type="text"
                placeholder={t('createTenant.displayNamePlaceholder')}
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
              />
              <div className="flex gap-2">
                <Button onClick={handleCreateCustomer} size="sm" className="flex-1">
                  {t('createTenant.create')}
                </Button>
                <Button onClick={() => setShowCreateForm(false)} variant="outline" size="sm" className="flex-1">
                  {t('createTenant.cancel')}
                </Button>
              </div>
            </div>
          )}
          <ul className="space-y-2">
            {visibleCustomers.map((c) => (
              <li key={c.customer_id}>
                <button
                  onClick={() => setSelectedId(c.customer_id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                    c.is_active === false ? 'opacity-60' : ''
                  } ${
                    selectedId === c.customer_id
                      ? 'border-primary-400 bg-primary-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex-shrink-0 p-1.5 rounded-md ${
                        selectedId === c.customer_id ? 'bg-primary-100' : 'bg-gray-100'
                      }`}
                    >
                      <Building2
                        className={`w-3.5 h-3.5 ${
                          selectedId === c.customer_id ? 'text-primary-600' : 'text-gray-500'
                        }`}
                      />
                    </span>
                    <span className="text-sm font-medium text-gray-900">{c.display_name}</span>
                    {c.is_active === false && <StatusChip tone="gray">{t('tenants.inactiveBadge')}</StatusChip>}
                  </div>
                  <TenantStatusChips summary={boardSummary[c.customer_id]} t={t} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        {selectedId && (
          <TabContainer
            className="flex-1"
            tabs={[
              { id: 'users', label: t('tenants.tabs.users') },
              { id: 'overview', label: t('tenants.tabs.overview') },
              { id: 'tileAccess', label: t('tenants.tabs.tileAccess') },
              { id: 'onboarding', label: t('onboarding.title') },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            navProps={{ allowCreate: false, allowRemove: false }}
          >
            {activeTab === 'users' && <UsersTab customerId={selectedId} />}
            {activeTab === 'overview' && (
              <OverviewTab
                customerId={selectedId}
                customer={customers.find((c) => c.customer_id === selectedId)}
                onSaved={(isActive) =>
                  setCustomers((prev) =>
                    prev.map((c) => (c.customer_id === selectedId ? { ...c, is_active: isActive } : c)),
                  )
                }
              />
            )}
            {activeTab === 'tileAccess' && (
              <TileAccessTab
                key={selectedId}
                customerId={selectedId}
                customer={customers.find((c) => c.customer_id === selectedId)}
                onSaved={(newAllowed) =>
                  setCustomers((prev) =>
                    prev.map((c) =>
                      c.customer_id === selectedId ? { ...c, allowed_component_ids: newAllowed } : c,
                    ),
                  )
                }
              />
            )}
            {activeTab === 'onboarding' && (
              <OnboardingTab
                key={selectedId}
                customerId={selectedId}
                customer={customers.find((c) => c.customer_id === selectedId)}
                onSaved={(newOnboardingStatus) =>
                  setCustomers((prev) =>
                    prev.map((c) =>
                      c.customer_id === selectedId ? { ...c, onboarding_status: newOnboardingStatus } : c,
                    ),
                  )
                }
              />
            )}
          </TabContainer>
        )}
      </div>
      )}
    </div>
  );
};

export default TenantAdminPage;
